import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { fmtDate } from "@/lib/utils";
import {
  Camera,
  Scissors,
  Users,
  Bell,
  AlertOctagon,
  AlertTriangle,
  Info,
  ArrowUpRight,
  CalendarClock,
  IndianRupee,
  UserMinus,
  ClipboardCheck,
} from "lucide-react";

/**
 * Iter127 — Today's Command Center
 * ------------------------------------------------------------------
 * One-glance "mission control" at the top of the Dashboard. Answers
 * "what does the studio owe attention to today?" in ≤10s of reading.
 *
 * Structure:
 *   1. Focus Strip     → compact horizontal counters
 *   2. Today's Tasks   → three cards (Shoots · Editors · Coordinators)
 *   3. Notification Center → Critical / Important / Info buckets
 *
 * All data is derived on the backend from live collections. Financial
 * items are masked entirely for non-admin roles (managers see counts
 * only via the `financial_summary` synthetic row).
 */

const URGENCY_META = {
  critical: {
    label: "Critical",
    tone: "bg-red-50 text-red-800 border-red-200",
    dot: "bg-red-600",
    Icon: AlertOctagon,
  },
  important: {
    label: "Important",
    tone: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    Icon: AlertTriangle,
  },
  info: {
    label: "Information",
    tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-500",
    Icon: Info,
  },
};

function FocusChip({ icon: Icon, label, value, tone, testid }) {
  if (value === undefined || value === null) return null;
  return (
    <div
      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border ${tone} min-w-fit`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg font-heading font-semibold tabular-nums leading-none">{value}</span>
        <span className="text-[11px] uppercase tracking-wider opacity-80">{label}</span>
      </div>
    </div>
  );
}

function ShootRow({ shoot }) {
  const alerts = [];
  if (!shoot.has_full_crew)
    alerts.push({
      text: `${shoot.crew_assigned_count}/${shoot.crew_required_count} crew`,
      tone: "bg-red-100 text-red-800",
    });
  if (!shoot.has_shoot_lead)
    alerts.push({ text: "no lead", tone: "bg-red-100 text-red-800" });

  return (
    <li
      className="px-4 py-3 hover:bg-stone-50/60 transition-colors"
      data-testid={`cc-shoot-${shoot.booking_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-stone-900 truncate">
            📸 {shoot.couple_name}
          </div>
          <div className="text-xs text-stone-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-1">
            <span>{shoot.event}</span>
            {shoot.location && <span>· {shoot.location}</span>}
            {shoot.call_time && (
              <span className="text-stone-700 font-medium">⏰ {shoot.call_time}</span>
            )}
            <span>· Lead: {shoot.shoot_lead_name || <em className="text-red-700">unassigned</em>}</span>
          </div>
          {alerts.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {alerts.map((a, i) => (
                <span
                  key={i}
                  className={`text-[10px] rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider ${a.tone}`}
                >
                  {a.text}
                </span>
              ))}
            </div>
          )}
        </div>
        <Link
          to={shoot.cta_link}
          className="text-[11px] text-rose-800 font-semibold hover:underline inline-flex items-center gap-1 shrink-0"
          data-testid={`cc-shoot-cta-${shoot.booking_id}`}
        >
          Manage <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
        </Link>
      </div>
    </li>
  );
}

function EditorRow({ editor }) {
  const preview = editor.tasks.slice(0, 3);
  const remainder = editor.tasks.length - preview.length;
  return (
    <li className="px-4 py-3" data-testid={`cc-editor-${editor.staff_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-stone-900 truncate">
            🎬 {editor.staff_name || "—"}
          </div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            {editor.task_count} open {editor.task_count === 1 ? "task" : "tasks"}
          </div>
        </div>
        <Link
          to="/post-production"
          className="text-[11px] text-rose-800 font-semibold hover:underline inline-flex items-center gap-1 shrink-0"
        >
          Open kanban <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
        </Link>
      </div>
      <ul className="mt-2 space-y-1">
        {preview.map((t) => (
          <li
            key={t.task_id}
            className="text-[11px] bg-stone-50 border border-stone-100 rounded-md px-2 py-1 flex items-center justify-between gap-2"
          >
            <span className="truncate">
              <span className="font-medium">{t.label}</span>
              <span className="text-stone-400 mx-1">·</span>
              <span className="text-stone-600">{t.couple_name || "—"}</span>
            </span>
            <span
              className={`text-[9px] uppercase tracking-wider shrink-0 ${
                t.status === "in_progress" ? "text-amber-700" : "text-stone-500"
              }`}
            >
              {t.status.replace("_", " ")}
            </span>
          </li>
        ))}
        {remainder > 0 && (
          <li className="text-[10px] text-stone-500 italic">+{remainder} more</li>
        )}
      </ul>
    </li>
  );
}

function CoordRow({ item }) {
  const meta = URGENCY_META[item.urgency] || URGENCY_META.info;
  return (
    <li className="px-4 py-2.5" data-testid={`cc-coord-${item.type}-${item.ref_id}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-stone-900 truncate">{item.title}</div>
          <div className="text-[11px] text-stone-500 truncate">{item.detail}</div>
        </div>
        <Link
          to={item.cta_link}
          className="text-[11px] text-rose-800 font-semibold hover:underline shrink-0 whitespace-nowrap"
        >
          Open →
        </Link>
      </div>
    </li>
  );
}

function TaskCard({ title, icon: Icon, count, children, testid }) {
  return (
    <div className="card overflow-hidden flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-stone-50 to-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-800">
            <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
          </div>
          <h3 className="font-heading text-sm font-semibold">{title}</h3>
        </div>
        <span className="badge badge-neutral text-[10px]">{count}</span>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function NotifBucket({ urgency, items, testid }) {
  const meta = URGENCY_META[urgency];
  const { Icon } = meta;
  return (
    <div className="card overflow-hidden" data-testid={testid}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${meta.tone}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
          <h3 className="font-heading text-sm font-semibold">{meta.label}</h3>
        </div>
        <span className="text-[11px] font-semibold tabular-nums">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-xs text-stone-400">All clear ✨</div>
      ) : (
        <ul className="divide-y divide-stone-100 max-h-72 overflow-y-auto">
          {items.slice(0, 12).map((it, i) => (
            <li
              key={`${it.type}-${it.ref_id || i}`}
              className="px-4 py-2.5"
              data-testid={`cc-notif-${urgency}-${i}`}
            >
              <div className="flex items-start gap-2.5">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-stone-900 truncate">{it.title}</div>
                  <div className="text-[11px] text-stone-500 truncate">{it.detail}</div>
                </div>
                {it.cta_link && (
                  <Link
                    to={it.cta_link}
                    className="text-[11px] text-rose-800 font-semibold hover:underline shrink-0 whitespace-nowrap"
                  >
                    Open →
                  </Link>
                )}
              </div>
            </li>
          ))}
          {items.length > 12 && (
            <li className="px-4 py-2 text-[10px] text-stone-500 italic bg-stone-50">
              +{items.length - 12} more not shown
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/command-center")
      .then((r) => setData(r.data))
      .catch((e) => {
        logger.debug("command-center load failed:", e?.message);
        setErr(e?.response?.data?.detail || "Unable to load command center");
      });
  }, []);

  if (err) {
    return (
      <div className="card p-4 text-sm text-red-700" data-testid="command-center-error">
        {String(err)}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card p-6 text-sm text-stone-500" data-testid="command-center-loading">
        Loading command center…
      </div>
    );
  }

  const focus = data.focus_strip || {};
  const tasks = data.today_tasks || {};
  const notifs = data.notifications || { critical: [], important: [], info: [] };
  const isAdminScope = focus.payments_to_collect_today !== undefined;

  const shoots = tasks.shoots || [];
  const editors = tasks.editors || [];
  const coords = tasks.coordinators || [];
  const onLeave = tasks.staff_on_leave || [];

  return (
    <section className="space-y-4" data-testid="command-center">
      {/* Header + Focus Strip */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">
            Today · {fmtDate(data.date)}
          </div>
          <h2 className="font-heading text-2xl font-semibold text-stone-900 mt-0.5">
            Command Center
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FocusChip
            icon={Camera}
            label="Shoots"
            value={focus.shoots_today}
            tone="bg-rose-50 text-rose-800 border-rose-200"
            testid="focus-shoots"
          />
          <FocusChip
            icon={ClipboardCheck}
            label="Deliverables"
            value={focus.deliverables_due_today}
            tone="bg-amber-50 text-amber-800 border-amber-200"
            testid="focus-deliverables"
          />
          {isAdminScope && (
            <FocusChip
              icon={IndianRupee}
              label="Payments"
              value={focus.payments_to_collect_today}
              tone="bg-emerald-50 text-emerald-800 border-emerald-200"
              testid="focus-payments"
            />
          )}
          <FocusChip
            icon={AlertOctagon}
            label="Overdue"
            value={focus.overdue_tasks}
            tone="bg-red-50 text-red-800 border-red-200"
            testid="focus-overdue"
          />
          <FocusChip
            icon={UserMinus}
            label="On Leave"
            value={focus.staff_on_leave}
            tone="bg-stone-50 text-stone-700 border-stone-200"
            testid="focus-onleave"
          />
        </div>
      </div>

      {/* Today's Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TaskCard
          title="Today's Shoots"
          icon={Camera}
          count={shoots.length}
          testid="today-tasks-shoots"
        >
          {shoots.length === 0 ? (
            <div className="p-6 text-center text-xs text-stone-400">
              No shoots today ✨
            </div>
          ) : (
            <ul className="divide-y divide-stone-100 max-h-80 overflow-y-auto">
              {shoots.map((s) => (
                <ShootRow key={s.booking_id} shoot={s} />
              ))}
            </ul>
          )}
        </TaskCard>

        <TaskCard
          title="Editors on Deck"
          icon={Scissors}
          count={editors.length}
          testid="today-tasks-editors"
        >
          {editors.length === 0 ? (
            <div className="p-6 text-center text-xs text-stone-400">
              No editor tasks in flight
            </div>
          ) : (
            <ul className="divide-y divide-stone-100 max-h-80 overflow-y-auto">
              {editors.slice(0, 8).map((e) => (
                <EditorRow key={e.staff_id} editor={e} />
              ))}
            </ul>
          )}
        </TaskCard>

        <TaskCard
          title="Coordinator Follow-Ups"
          icon={Users}
          count={coords.length}
          testid="today-tasks-coordinators"
        >
          {coords.length === 0 ? (
            <div className="p-6 text-center text-xs text-stone-400">
              No follow-ups pending — you're on top of it ✨
            </div>
          ) : (
            <ul className="divide-y divide-stone-100 max-h-80 overflow-y-auto">
              {coords.slice(0, 12).map((c, i) => (
                <CoordRow key={`${c.type}-${c.ref_id || i}`} item={c} />
              ))}
              {coords.length > 12 && (
                <li className="px-4 py-2 text-[10px] text-stone-500 italic bg-stone-50">
                  +{coords.length - 12} more
                </li>
              )}
            </ul>
          )}
        </TaskCard>
      </div>

      {/* Notification Center */}
      <div className="flex items-center gap-2 pt-2">
        <Bell className="w-4 h-4 text-stone-500" strokeWidth={1.8} />
        <h3 className="font-heading text-base font-semibold text-stone-800">
          Notification Center
        </h3>
        <span className="text-[11px] text-stone-500">
          {data.counts?.critical || 0} critical · {data.counts?.important || 0} important · {data.counts?.info || 0} info
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <NotifBucket urgency="critical" items={notifs.critical || []} testid="notif-critical" />
        <NotifBucket urgency="important" items={notifs.important || []} testid="notif-important" />
        <NotifBucket urgency="info" items={notifs.info || []} testid="notif-info" />
      </div>

      {/* Staff on leave strip (Admin+Manager+Coord) */}
      {onLeave.length > 0 && (
        <div
          className="card px-4 py-3 flex items-center gap-3 flex-wrap bg-stone-50/70 border-stone-200"
          data-testid="staff-on-leave-strip"
        >
          <UserMinus className="w-4 h-4 text-stone-500" strokeWidth={1.8} />
          <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">
            On leave today
          </span>
          <div className="flex flex-wrap gap-1.5">
            {onLeave.map((s) => (
              <span
                key={s.staff_id}
                className="text-[11px] bg-white border border-stone-200 rounded-full px-2.5 py-0.5 text-stone-700"
              >
                {s.staff_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
